// frontend/src/components/client/Email/SpamFilterTab.tsx
/**
 * Per-mailbox spam allow/deny list editor.
 *
 * A pattern is either:
 *  - a full email address  → matches one sender
 *  - `@domain.tld`         → matches every sender on that domain
 *
 * Allow rules win over deny when both match (Rspamd convention).
 */
import React, { useState } from 'react';

import {
  emailSpamAPI,
  type SpamRule,
  type SpamRuleKind,
} from '@hpanel/web-common/api/email_spam';
import type { Mailbox } from '@hpanel/web-common/api/types';

interface Props {
  mailboxes: Mailbox[];
  rules: SpamRule[];
  mailboxAddress: (mb: Mailbox) => string;
  onChange: () => Promise<void>;
  onError: (msg: string | null) => void;
}

export const SpamFilterTab: React.FC<Props> = ({
  mailboxes,
  rules,
  mailboxAddress,
  onChange,
  onError,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(
    mailboxes[0]?.id ?? null,
  );
  const [pattern, setPattern] = useState('');
  const [kind, setKind] = useState<SpamRuleKind>('deny');
  const [busy, setBusy] = useState(false);

  const rulesForBox = (id: number, k: SpamRuleKind) =>
    rules.filter((r) => r.mailbox_id === id && r.kind === k);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedId == null || !pattern.trim()) return;
    setBusy(true);
    onError(null);
    try {
      await emailSpamAPI.addRule(selectedId, {
        kind,
        pattern: pattern.trim(),
      });
      setPattern('');
      await onChange();
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (ruleId: number) => {
    setBusy(true);
    onError(null);
    try {
      await emailSpamAPI.deleteRule(ruleId);
      await onChange();
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (mailboxes.length === 0) {
    return (
      <div
        data-testid="spam-empty"
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-sm text-gray-600 dark:text-gray-400"
      >
        Create a mailbox first — spam filters sit on individual addresses.
      </div>
    );
  }

  const allow = selectedId != null ? rulesForBox(selectedId, 'allow') : [];
  const deny = selectedId != null ? rulesForBox(selectedId, 'deny') : [];

  return (
    <div data-testid="spam-tab" className="space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Mailbox
          </label>
          <select
            data-testid="spam-mailbox"
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            className="w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
          >
            {mailboxes.map((mb) => (
              <option key={mb.id} value={mb.id}>
                {mailboxAddress(mb)}
              </option>
            ))}
          </select>
        </div>

        <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            data-testid="spam-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as SpamRuleKind)}
            className="px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
          >
            <option value="deny">Block</option>
            <option value="allow">Allow</option>
          </select>
          <input
            data-testid="spam-pattern"
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="alice@example.com or @spammer.net"
            maxLength={255}
            className="md:col-span-2 px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
          />
          <button
            data-testid="spam-add"
            type="submit"
            disabled={busy || !pattern.trim()}
            className="px-4 py-2 text-sm rounded-md bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
          >
            Add rule
          </button>
        </form>
        <p className="text-xs text-gray-500">
          Use a full email to block a single sender, or
          <code className="mx-1 px-1 bg-gray-100 dark:bg-gray-800 rounded">@domain.tld</code>
          to block everyone on that domain. Allow always wins over Block.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RuleList
          title="Allow"
          rules={allow}
          empty="No allow rules yet."
          onDelete={handleDelete}
          accentClass="text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
          testId="spam-allow-list"
        />
        <RuleList
          title="Block"
          rules={deny}
          empty="No block rules yet."
          onDelete={handleDelete}
          accentClass="text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
          testId="spam-deny-list"
        />
      </div>
    </div>
  );
};

interface RuleListProps {
  title: string;
  rules: SpamRule[];
  empty: string;
  accentClass: string;
  testId: string;
  onDelete: (id: number) => void;
}

const RuleList: React.FC<RuleListProps> = ({
  title,
  rules,
  empty,
  accentClass,
  testId,
  onDelete,
}) => (
  <div
    data-testid={testId}
    className={`bg-white dark:bg-gray-800 border rounded-lg p-4 ${accentClass}`}
  >
    <h3 className="text-sm font-semibold mb-2">
      {title} ({rules.length})
    </h3>
    {rules.length === 0 ? (
      <p className="text-xs text-gray-500">{empty}</p>
    ) : (
      <ul className="space-y-1">
        {rules.map((r) => (
          <li
            key={r.id}
            data-testid={`spam-rule-${r.id}`}
            className="flex items-center justify-between gap-2 text-sm"
          >
            <code className="font-mono break-all">{r.pattern}</code>
            <button
              data-testid={`spam-delete-${r.id}`}
              onClick={() => onDelete(r.id)}
              className="text-xs text-gray-500 hover:text-red-600"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);
